import type { FC, ReactNode } from "react";
import { AlertCircle, X } from "@untitledui/icons";
import { Button, ButtonUtility, FeaturedIcon, cx } from "@tricura/atlas-ui";

interface ActionDescriptor {
    label: ReactNode;
    onClick: () => void;
    iconTrailing?: FC<{ className?: string }>;
}

interface FeaturedAlertProps {
    /** Icon at the top-left. Defaults to the canonical `AlertCircle`
     *  warning glyph used by the "Duplicate Submission Detected" Figma. */
    icon?: FC<{ className?: string }>;
    /** Atlas semantic colour family — drives the rings + icon hue.
     *  Defaults to `warning` (yellow) to match the duplication alert. */
    color?: "brand" | "gray" | "success" | "warning" | "error";
    /** Bold one-liner. */
    title: ReactNode;
    /** Optional supporting copy below the title. */
    description?: ReactNode;
    /** Right-side CTA — typically the primary action ("Review duplicates"). */
    primaryAction?: ActionDescriptor;
    /** Left-side secondary action — typically "Dismiss". Rendered as a
     *  text-only link-gray button. */
    secondaryAction?: ActionDescriptor;
    /** Top-right close button. Omit for non-dismissible alerts. */
    onClose?: () => void;
    className?: string;
    /** Custom attribute for E2E testing. */
    "data-autotest"?: string;
}

/**
 * Card-style alert with a haloed featured icon — implements the
 * "Alert with Featured icon outline" Figma pattern
 * (`Warning for Deal Duplication`, node 95:28243).
 *
 * Atlas-ui's `FeaturedIcon` with `theme="outline"` already renders the
 * inner + outer concentric rings; we override the colour tokens so
 * the rings + icon land on exact `#EAB308` (Tailwind `yellow-500`)
 * instead of the slightly darker `warning-primary` (yellow-600) the
 * semantic mapping defaults to.
 */
export function FeaturedAlert({
    icon: Icon = AlertCircle,
    color = "warning",
    title,
    description,
    primaryAction,
    secondaryAction,
    onClose,
    className,
    "data-autotest": dataAutotest,
}: FeaturedAlertProps) {
    // Per-color override to lock the icon + rings to the brand-tier
    // utility tokens (the `*-500` shade) so the alert matches the
    // saturation in Figma. `color="warning"` semantically maps to
    // `yellow-600` which reads visibly darker than the spec.
    const overrideForWarning =
        color === "warning"
            ? "text-utility-yellow-500 before:border-utility-yellow-500/30 after:border-utility-yellow-500/10"
            : "";

    return (
        <div
            role="alert"
            data-autotest={dataAutotest}
            className={cx(
                "bg-primary border-secondary relative flex items-start gap-4 rounded-xl border p-4",
                className,
            )}
        >
            <FeaturedIcon
                icon={Icon}
                color={color}
                theme="outline"
                size="md"
                className={overrideForWarning}
            />

            <div className={cx("flex flex-1 flex-col gap-3", onClose && "pr-8")}>
                <div className="flex flex-col gap-1">
                    <h3 className="text-secondary text-sm font-semibold">{title}</h3>
                    {description && <p className="text-tertiary text-sm">{description}</p>}
                </div>

                {(primaryAction || secondaryAction) && (
                    <div className="flex flex-wrap items-center gap-3">
                        {secondaryAction && (
                            <Button color="link-gray" size="sm" onClick={secondaryAction.onClick}>
                                {secondaryAction.label}
                            </Button>
                        )}
                        {primaryAction && (
                            <Button
                                color="secondary"
                                size="sm"
                                iconTrailing={primaryAction.iconTrailing}
                                onClick={primaryAction.onClick}
                            >
                                {primaryAction.label}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {onClose && (
                <ButtonUtility
                    color="tertiary"
                    size="sm"
                    icon={X}
                    onClick={onClose}
                    tooltip="Close"
                    data-autotest={dataAutotest ? `${dataAutotest}-close-button` : undefined}
                    // Figma's `Buttons/Button close X`: 36×36, radius-md,
                    // padding spacing-md. `ButtonUtility` ships `p-1.5`,
                    // which lands at 32px — `p-2` around the 20px glyph is
                    // the missing 4px. `cx` is tailwind-merge, so the
                    // override wins without `!`.
                    //
                    // Offset by the same 8px so the GLYPH, not the button
                    // box, lines up with the card's 16px content inset.
                    className="absolute top-2 right-2 p-2"
                />
            )}
        </div>
    );
}
